import { useEffect, useMemo, useRef } from "react";
import { Chess } from "chess.js";
import { Button } from "@/components/ui/button";
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react";

// Replays UCI/SAN moves from the start position; returns [{san, fen, from, to}] with index 0 = start.
export const replayMoves = (moves) => {
  const chess = new Chess();
  const steps = [{ san: null, fen: chess.fen(), from: null, to: null }];
  for (const raw of moves || []) {
    let m = null;
    try {
      m = /^[a-h][1-8][a-h][1-8][qrbn]?$/.test(raw)
        ? chess.move({ from: raw.slice(0, 2), to: raw.slice(2, 4), promotion: raw[4] || undefined })
        : chess.move(raw);
    } catch {
      m = null;
    }
    if (!m) break;
    steps.push({ san: m.san, fen: chess.fen(), from: m.from, to: m.to });
  }
  return steps;
};

// `viewIndex` = null means live position; otherwise index into steps (0 = start).
export const MoveList = ({ steps, viewIndex, onSelect, className = "", height = "h-48" }) => {
  const live = steps.length - 1;
  const current = viewIndex ?? live;
  const activeRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const el = activeRef.current, box = listRef.current;
    if (!el || !box) return;
    const top = el.offsetTop - box.offsetTop;
    if (top < box.scrollTop) box.scrollTop = top;
    else if (top + el.offsetHeight > box.scrollTop + box.clientHeight) box.scrollTop = top + el.offsetHeight - box.clientHeight;
  }, [current]);

  const rows = useMemo(() => {
    const out = [];
    for (let i = 1; i < steps.length; i += 2) {
      out.push({ num: (i + 1) / 2, white: { idx: i, san: steps[i].san }, black: steps[i + 1] ? { idx: i + 1, san: steps[i + 1].san } : null });
    }
    return out;
  }, [steps]);

  const go = (idx) => {
    const clamped = Math.max(0, Math.min(live, idx));
    onSelect(clamped === live ? null : clamped);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) return;
      e.preventDefault();
      if (e.key === "ArrowLeft") go(current - 1);
      else if (e.key === "ArrowRight") go(current + 1);
      else if (e.key === "ArrowUp") go(0);
      else if (e.key === "ArrowDown") go(live);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const Cell = ({ m, white }) =>
    m ? (
      <button
        ref={m.idx === current ? activeRef : null}
        type="button"
        onClick={() => go(m.idx)}
        data-testid={`move-ply-${m.idx}`}
        className={`w-16 text-left px-1.5 py-0.5 rounded font-mono text-xs sm:text-sm transition-colors
          ${m.idx === current ? "bg-primary text-primary-foreground font-bold" : white ? "text-white hover:bg-muted" : "text-zinc-400 hover:bg-muted"}`}
      >
        {m.san}
      </button>
    ) : (
      <span className="w-16" />
    );

  return (
    <div className={className} data-testid="move-list">
      <div ref={listRef} className={`${height} overflow-y-auto pr-1 relative`}>
        {rows.length === 0 ? (
          <p className="text-xs sm:text-sm text-muted-foreground text-center py-4">—</p>
        ) : (
          rows.map((r) => (
            <div key={r.num} className="flex items-center gap-1 py-0.5" data-testid={`move-${r.num}`}>
              <span className="text-muted-foreground w-6 text-right text-xs sm:text-sm font-mono">{r.num}.</span>
              <Cell m={r.white} white />
              <Cell m={r.black} />
            </div>
          ))
        )}
      </div>
      {live > 0 && (
        <div className="flex items-center justify-between gap-1 pt-2 mt-2 border-t border-border/50">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => go(0)} disabled={current === 0} data-testid="nav-first"><ChevronsLeft className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => go(current - 1)} disabled={current === 0} data-testid="nav-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-[11px] text-muted-foreground font-mono tabular-nums" data-testid="nav-position">
            {viewIndex === null || viewIndex === undefined ? "LIVE" : `${current}/${live}`}
          </span>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => go(current + 1)} disabled={current >= live} data-testid="nav-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => go(live)} disabled={current >= live} data-testid="nav-last"><ChevronsRight className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
};

export default MoveList;
